import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';

import type {
    PurchaseOrderRequestQuery,
    PurchaseOrderRequestQueryVariables,
    SubmitPurchaseOrderNumberMutation,
    SubmitPurchaseOrderNumberMutationVariables,
} from '@/graphql/types';
import { PurchaseOrderRequestDocument, SubmitPurchaseOrderNumberDocument } from '@/graphql/types';
import { BasePage } from '../BasePage';
import { ErrorPage } from '../ErrorPage';
import { LoadingPage } from '../LoadingPage';

type PurchaseOrderRequestPageProps = {
    token: string;
};

export default function PurchaseOrderRequestPage({ token }: PurchaseOrderRequestPageProps) {
    const [draftPoNumber, setDraftPoNumber] = useState<string>();
    const [submitted, setSubmitted] = useState(false);
    const pageTitle = gettext('PO_NUMBER_PAGE_TITLE');

    const { data, loading, error } = useQuery<
        PurchaseOrderRequestQuery,
        PurchaseOrderRequestQueryVariables
    >(PurchaseOrderRequestDocument, {
        variables: { token },
        fetchPolicy: 'network-only',
    });

    const request = data?.purchaseOrderRequest;
    const poNumber = request?.poNumber ?? '';
    const inputValue = draftPoNumber ?? poNumber;
    const subscriptionNames = request?.subscriptionNames ?? [];
    const invoiceReference = request?.invoiceReference ?? '';

    const [submitPoNumber, { loading: submitting, error: submitError }] = useMutation<
        SubmitPurchaseOrderNumberMutation,
        SubmitPurchaseOrderNumberMutationVariables
    >(SubmitPurchaseOrderNumberDocument, {
        update(cache, { data: mutationData }) {
            const updatedRequest = mutationData?.submitPurchaseOrderNumber?.request;

            if (!updatedRequest) {
                return;
            }

            cache.writeQuery<PurchaseOrderRequestQuery, PurchaseOrderRequestQueryVariables>({
                query: PurchaseOrderRequestDocument,
                variables: { token },
                data: {
                    purchaseOrderRequest: updatedRequest,
                },
            });
        },
        onCompleted: (mutationData) => {
            if (!mutationData.submitPurchaseOrderNumber?.ok) {
                return;
            }

            setDraftPoNumber(undefined);
            setSubmitted(true);
        },
    });

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        setSubmitted(false);

        const trimmed = inputValue.trim();
        void submitPoNumber({
            variables: {
                token,
                poNumber: trimmed || null,
            },
        });
    }

    const currentPoDisplay = poNumber || gettext('PO_NUMBER_NOT_SET');

    const basePageProps = {
        title: pageTitle,
        className: 'main-container',
    };

    if (loading) {
        return <LoadingPage />;
    }

    if (error || !request) {
        return <ErrorPage {...basePageProps} error={error} />;
    }

    if (request.isExpired) {
        return (
            <BasePage {...basePageProps}>
                <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-600">
                    {gettext('PO_NUMBER_LINK_EXPIRED')}
                </div>
            </BasePage>
        );
    }

    return (
        <BasePage {...basePageProps}>
            <div className="w-full max-w-2xl">
                <form id="po-number-request-form" className="space-y-6" onSubmit={handleSubmit}>
                    <p className="text-form-text text-base">{gettext('PO_NUMBER_SUMMARY')}</p>

                    <div className="space-y-2">
                        <p className="block text-xs font-semibold tracking-wide text-slate-500">
                            {gettext('PO_NUMBER_INVOICE_LABEL')}
                        </p>
                        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
                            <p className="text-sm font-semibold text-slate-800">
                                {invoiceReference || gettext('PO_NUMBER_NOT_SET')}
                            </p>
                        </div>
                    </div>

                    {subscriptionNames.length > 0 && (
                        <div className="space-y-2">
                            <p className="block text-xs font-semibold tracking-wide text-slate-500">
                                {gettext('PO_NUMBER_SUBSCRIPTIONS_LABEL')}
                            </p>
                            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
                                <ul className="list-disc space-y-1 pl-5 text-sm text-slate-800">
                                    {subscriptionNames.map((name) => (
                                        <li key={name}>{name}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    <div className="space-y-2">
                        <p className="block text-xs font-semibold tracking-wide text-slate-500">
                            {gettext('PO_NUMBER_CURRENT_LABEL')}
                        </p>
                        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
                            <p className="text-lg font-semibold text-slate-800">
                                {currentPoDisplay}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label
                            htmlFor="po-number-input"
                            className="block text-sm font-medium text-slate-700"
                        >
                            {gettext('PO_NUMBER_FIELD_LABEL')}
                        </label>
                        <input
                            id="po-number-input"
                            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-100"
                            type="text"
                            value={inputValue}
                            onChange={(event) => setDraftPoNumber(event.target.value)}
                            placeholder={gettext('PO_NUMBER_PLACEHOLDER')}
                            autoComplete="off"
                            maxLength={100}
                        />
                    </div>

                    {submitted && !submitError && (
                        <div className="rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                            {gettext('PO_NUMBER_SAVE_SUCCESS')}
                        </div>
                    )}

                    {submitError && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-600">
                            {gettext('PO_NUMBER_SAVE_ERROR')}
                        </div>
                    )}

                    <div>
                        <button
                            className="bg-primary hover:bg-button-hover focus:ring-primary/40 inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-slate-900 transition focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
                            type="submit"
                            disabled={submitting}
                        >
                            {gettext('PO_NUMBER_SAVE_BUTTON')}
                        </button>
                    </div>
                </form>
            </div>
        </BasePage>
    );
}
